import { useEffect } from "react";
import { getDynoWs } from "@/lib/ws";
import { useLiveFeed, type DynoSamplesIncoming } from "@/stores/liveFeed";
import { useEventQueue } from "@/stores/eventQueue";
import { record as recordPerf, recordChange } from "@/lib/realtimePerf";
import { recordSampleChanges } from "@/hooks/useDynoMqtt";
import type { Service, PqEvent } from "@/lib/types";

// Bridge ws.ts → Zustand stores. Module-level singleton handles reconnect;
// this hook just sets up one subscription per mount and tears it down on
// unmount. Keep the socket outside React so StrictMode double-mount in dev
// doesn't cause double connections.

interface DynoSnapshotMsg {
  type: "dyno_snapshot";
  ts: number;
  services: Record<
    string,
    {
      v?: (number | null)[];
      i?: (number | null)[];
      p?: number | null;
      s?: number | null;
      pf?: number | null;
      f?: number | null;
      i_n?: number | null;
      demand_w?: number | null;
      demand_source?: "meter" | "derived" | null;
      meter_model?: string | null;
      computed_fields?: string[];
      p_phase?: (number | null)[];
      s_phase?: (number | null)[];
      pf_phase?: (number | null)[];
      ts?: number;
      node_id?: string;
      window_id?: string | null;
      sig?: "ok" | "stale";
      // PQ-tuning enrichment (ws_realtime._build_dyno_snapshot) — drives the
      // PqEventBadge + tuning dialog. Always present on the wire.
      inominal_amps?: number | null;
      inominal_status?: "syncing" | "ok" | "failed" | "invalid" | null;
      flagged?: boolean | null;
      reason?: "upward_noise" | "downward_miss" | "ok" | null;
      dominant_severity?: "severe" | "moderate" | "minor" | "none" | null;
      events_last_hour?: number | null;
    }
  >;
}

interface PqEventMsg {
  type: "pq_event_keep" | "pq_event_drop";
  event: Partial<PqEvent> & { event_id: string };
}

// 600ms fast-cadence multi-sample batches from the edge dyno_samples
// publisher. Per-system columnar arrays — see _DYNO_FAST_FIELDS in
// edge/services/events/app.py. Drives the sparkline + tile realtime feel.
interface DynoSamplesMsg {
  type: "dyno_samples";
  node_id: string;
  ts: number;
  systems: DynoSamplesIncoming[];
}

function messageIsSnapshot(m: unknown): m is DynoSnapshotMsg {
  return !!m && typeof m === "object" && (m as DynoSnapshotMsg).type === "dyno_snapshot";
}

function messageIsSamples(m: unknown): m is DynoSamplesMsg {
  return !!m && typeof m === "object" && (m as DynoSamplesMsg).type === "dyno_samples";
}

function messageIsPqEvent(m: unknown): m is PqEventMsg {
  if (!m || typeof m !== "object") return false;
  const t = (m as PqEventMsg).type;
  return t === "pq_event_keep" || t === "pq_event_drop";
}

export function useDynoWs({ enabled = true }: { enabled?: boolean } = {}) {
  const applySnapshot = useLiveFeed((s) => s.applySnapshot);
  const applyDynoSamples = useLiveFeed((s) => s.applyDynoSamples);
  const setConnected = useLiveFeed((s) => s.setConnected);
  const ingestEvent = useEventQueue((s) => s.ingest);

  useEffect(() => {
    if (!enabled) return;
    const ws = getDynoWs();
    ws.connect();

    // Subscribe to dyno topic on connect. The ws singleton re-fires subscribe
    // on each reconnect internally (see lib/ws.ts onopen handler).
    const unsub = ws.subscribe((msg) => {
      if (messageIsSamples(msg)) {
        // Fast path: 600ms multi-sample batches (~3 samples per system per
        // message). Lifts the latest sample into the per-service scalars
        // (so the tile's % capacity / DEMAND / PF / Hz update from the
        // freshest data) and stashes the full batch for sparkline drain.
        recordPerf("ws", "dyno_samples", msg.ts);
        applyDynoSamples(msg.systems);
        recordSampleChanges("ws", "dyno_samples", msg.systems);
        setConnected(true);
        return;
      }
      if (messageIsSnapshot(msg)) {
        recordPerf("ws", "dyno_snapshot", msg.ts);
        // dyno_snapshot is a per-service Service-shaped dict; record per-
        // (system, field) changes from the snapshot too. The cloud-api
        // sources its `ts` from time.time()*1000 at broadcast (not edge
        // ts), so the latency for these change events reflects cloud-api
        // assemble + broadcast lag, not the edge-to-cloud-to-browser path.
        // Useful as a sanity check vs the dyno_samples row.
        for (const [sid, s] of Object.entries(msg.services)) {
          const edgeTs = (s.ts != null ? s.ts : msg.ts);
          if (typeof edgeTs !== "number") continue;
          if (s.p != null) recordChange("ws", "dyno_snapshot", `${sid}:p`, s.p, edgeTs);
          if (s.i != null && s.i.length === 3) {
            const iPeak = Math.max(...s.i.filter((v): v is number => v != null));
            if (Number.isFinite(iPeak)) {
              recordChange("ws", "dyno_snapshot", `${sid}:i_peak`, iPeak, edgeTs);
            }
          }
        }
        const services: Service[] = Object.entries(msg.services).map(
          ([system_id, s]) => {
            // Minimal Service fields populated from WS — Dashboard enriches
            // with static fields (service_name, node_id, etc.) from /api/services.
            //
            // OMIT live-scalar fields (v/i/p/s/pf/f/i_n/*_phase) when the
            // snapshot has null/undefined values. The downstream applySnapshot
            // does `{...prev, ...svc}`, so an omitted field preserves prev
            // (which may have just been set by applyDynoSamples from a fresh
            // dyno_samples MQTT message). Including null would clobber prev
            // and cause tile flicker between "—" and the real value.
            const svc: Service = {
              membership_id: "",
              window_id: s.window_id ?? "",
              system_id,
              service_name: null,
              service_capacity_amps: null,
              system_type: "acuvim",
              node_id: s.node_id ?? null,
              joined_at: null,
              left_at: null,
              location: null,
              demand_w: s.demand_w ?? null,
              demand_source: s.demand_source ?? null,
              meter_model: s.meter_model ?? null,
              computed_fields: s.computed_fields ?? [],
              sig: s.sig ?? "ok",
              // PQ-tuning fields: always carried from the snapshot. Unlike the
              // live scalars above there is no competing writer (applyDynoSamples
              // never sets them), so copy unconditionally — null is a valid
              // "not flagged / no data" state, not an omit-to-preserve case.
              inominal_amps: s.inominal_amps ?? null,
              inominal_status: s.inominal_status ?? null,
              flagged: s.flagged ?? null,
              reason: s.reason ?? null,
              dominant_severity: s.dominant_severity ?? null,
              events_last_hour: s.events_last_hour ?? null,
            };
            const v = normalizeTriple(s.v);
            if (v) svc.v = v;
            const i = normalizeTriple(s.i);
            if (i) svc.i = i;
            if (s.p != null) svc.p = s.p;
            if (s.s != null) svc.s = s.s;
            if (s.pf != null) svc.pf = s.pf;
            if (s.f != null) svc.f = s.f;
            if (s.i_n != null) svc.i_n = s.i_n;
            if (s.p_phase) svc.p_phase = s.p_phase as Service["p_phase"];
            if (s.s_phase) svc.s_phase = s.s_phase as Service["s_phase"];
            if (s.pf_phase) svc.pf_phase = s.pf_phase as Service["pf_phase"];
            // Source-payload epoch ms — drives the latency UI in AdminLoadIn.
            if (s.ts != null) svc.ts = s.ts;
            return svc;
          }
        );
        applySnapshot(services);
        setConnected(true);
      } else if (messageIsPqEvent(msg)) {
        ingestEvent({
          ...(msg.event as PqEvent),
          decision: msg.type === "pq_event_keep" ? "keep" : "drop",
        } as PqEvent);
      }
    });

    return () => {
      unsub();
      // Don't disconnect — other tabs / reloads reuse the singleton.
      // The singleton closes when the window unloads.
    };
  }, [enabled, applySnapshot, applyDynoSamples, setConnected, ingestEvent]);
}

function normalizeTriple(
  v: (number | null)[] | undefined
): [number, number, number] | undefined {
  if (!v || v.length !== 3) return undefined;
  if (v[0] == null || v[1] == null || v[2] == null) return undefined;
  return [v[0], v[1], v[2]];
}
